/**
 * InboxChatItem — direct-message conversation row of the Inbox list.
 *
 * Flutter source of truth: `MessagesChatItem`
 * (`lib/presentation/screens/messages/widgets/messages_chat_item.dart:29-216`).
 *
 * Layout (mirrors Flutter's `Row(avatar, Flexible(name+badge / preview·dot·time),
 * trailing seen-indicator)`):
 *   - Leading peer avatar — `generateUserProfileImageUrl(isCurrentUser ? idTo : idFrom)`
 *     (`messages_chat_item.dart:85-90`).
 *   - Title row — peer display name + `<IdentityBadge>` of the peer id
 *     (`messages_chat_item.dart:108-122`).
 *   - Subtitle row — `displayText` (single-line, ellipsized) · 2px dot · timestamp
 *     (`messages_chat_item.dart:123-157`).
 *   - Trailing — the four-way seen indicator (`messages_chat_item.dart:162-211`):
 *       own + seen   → tiny peer avatar.
 *       own + unseen → check-circle icon.
 *       peer + unseen→ red circle.
 *       peer + seen  → nothing.
 *
 * `displayText` varies by `ChatMessageType` and sender exactly as
 * `messages_chat_item.dart:34-82` — own → "You: …"/"You sent …", peer →
 * "<peerFirstName> sent …" where `peerFirstName = nameUser1.split(' ')[0]`.
 *
 * Pure renderer: the row-tap callback (`onTap`) and the current user id come
 * from the page (Task 15). All copy via l10n, all colors/sizes via tokens.
 *
 * Token deviations (documented per `presentation.md`): Flutter's seen-indicator
 * is a fixed 15×15 box; the closest web token is `responsive.icon.small` (16) —
 * the same choice `ReadReceiptAvatar.tsx` made for the chat read-receipt.
 */
import React from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { IdentityBadge } from '@/presentation/components/common/identity/IdentityBadge';
import { UserUtils } from '@/domain/utils/userUtils';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { getChatPreviewText } from './getChatPreviewText';
import type { ChatMessageNodeEntity } from '@/domain/entities/chat/chatMessageNodeEntity';

export interface InboxChatItemProps {
  node: ChatMessageNodeEntity;
  currentUserId: string;
  onTap: (node: ChatMessageNodeEntity) => void;
}

export const InboxChatItem: React.FC<InboxChatItemProps> = ({
  node,
  currentUserId,
  onTap,
}) => {
  const { l10n } = useLocale();
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { formatDate } = useDateFormatters();
  const c = l10n.inbox.chat;

  const isCurrentUser = currentUserId === node.idFrom;

  // displayText per ChatMessageType × sender (`messages_chat_item.dart:34-82`).
  const displayText = getChatPreviewText(node, isCurrentUser, c);

  // Always render the peer (`messages_chat_item.dart:85-90`).
  const peerId = isCurrentUser ? node.idTo : node.idFrom;
  const displayName = isCurrentUser ? node.nameUser2 : node.nameUser1;
  const peerAvatarUrl = UserUtils.generateUserProfileImageUrl(peerId);

  const indicatorSize = responsive.icon.small;

  // Four-way trailing seen indicator (`messages_chat_item.dart:162-211`).
  let trailing: React.ReactNode = null;
  if (isCurrentUser) {
    trailing = node.seenByPeer ? (
      // own + seen → tiny peer avatar.
      <CachedImage
        src={peerAvatarUrl}
        alt=""
        style={{
          width: indicatorSize,
          height: indicatorSize,
          borderRadius: responsive.borderRadius.round,
          objectFit: 'cover',
          flexShrink: 0,
        }}
      />
    ) : (
      // own + unseen → check-circle icon.
      <AssetIcon
        path="/assets/images/check.png"
        width={indicatorSize}
        height={indicatorSize}
        color={theme.disabled}
      />
    );
  } else if (!node.seenByPeer) {
    // peer + unseen → red circle (matches TopBar inbox dot + Flutter parity).
    trailing = (
      <div
        style={{
          width: indicatorSize,
          height: indicatorSize,
          borderRadius: responsive.borderRadius.round,
          backgroundColor: theme.activeRed,
          flexShrink: 0,
        }}
      />
    );
  }
  // peer + seen → nothing (trailing stays null).

  return (
    <div
      {...qaAttr({ id: `inbox-chat-${node.chatId}` })}
      onClick={() => onTap(node)}
      style={{
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
      }}
    >
      {/* Leading peer avatar */}
      <CachedImage
        src={peerAvatarUrl}
        alt={displayName}
        style={{
          width: responsive.avatar.medium,
          height: responsive.avatar.medium,
          borderRadius: responsive.borderRadius.round,
          objectFit: 'cover',
          flexShrink: 0,
        }}
      />

      {/* Name + badge / preview · dot · time */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          paddingLeft: responsive.spacing.sm,
          paddingRight: responsive.spacing.sm,
        }}
      >
        {/* Title row: name + identity badge */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: responsive.gap.xs,
            color: theme.text.primary,
            fontSize: responsive.font.base,
            fontWeight: responsive.fontWeight.medium,
          }}
        >
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayName}
          </span>
          <IdentityBadge userId={peerId} />
        </div>

        {/* Subtitle row: preview · dot · time */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: responsive.gap.xs,
            marginTop: responsive.spacing.xs,
            color: theme.text.secondary,
            fontSize: responsive.font.sm,
          }}
        >
          <span
            style={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayText}
          </span>
          <span style={{ color: theme.disabled, flexShrink: 0 }}>·</span>
          <span style={{ color: theme.disabled, flexShrink: 0, whiteSpace: 'nowrap' }}>
            {formatDate(node.timestamp)}
          </span>
        </div>
      </div>

      {/* Trailing seen indicator */}
      {trailing && <div style={{ flexShrink: 0 }}>{trailing}</div>}
    </div>
  );
};
